import { useState, useCallback, useEffect, useRef } from 'react'

/**
 * Validation rule types
 */
export type ValidationRule = 
  | { type: 'required'; message?: string }
  | { type: 'minLength'; value: number; message?: string }
  | { type: 'maxLength'; value: number; message?: string }
  | { type: 'pattern'; value: RegExp; message?: string }
  | { type: 'email'; message?: string }
  | { type: 'url'; message?: string }
  | { type: 'min'; value: number; message?: string }
  | { type: 'max'; value: number; message?: string }
  | { type: 'custom'; validator: (value: any, values?: any) => boolean | string; message?: string }
  | { type: 'async'; validator: (value: any, values?: any) => Promise<boolean | string>; message?: string }

/**
 * Validation schema type
 */
export interface ValidationSchema {
  [fieldName: string]: ValidationRule[]
}

/**
 * Hook options
 */
interface UseFormValidationOptions<T> {
  /** Initial form values */
  initialValues: T
  /** Validation schema */
  validationSchema?: ValidationSchema
  /** Form submit handler */
  onSubmit?: (values: T) => void | Promise<void>
  /** Validate on change */
  validateOnChange?: boolean
  /** Validate on blur */
  validateOnBlur?: boolean
  /** Validate on mount */
  validateOnMount?: boolean
}

/**
 * Hook return type
 */
interface UseFormValidationReturn<T> {
  /** Form values */
  values: T
  /** Form errors */
  errors: Partial<Record<keyof T, string>>
  /** Touched fields */
  touched: Partial<Record<keyof T, boolean>>
  /** Form is valid */
  isValid: boolean
  /** Form is submitting */
  isSubmitting: boolean
  /** Form is validating */
  isValidating: boolean
  /** Handle field change */
  handleChange: (name: keyof T) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | any) => void
  /** Handle field blur */
  handleBlur: (name: keyof T) => () => void
  /** Set field value */
  setFieldValue: (name: keyof T, value: any) => void
  /** Set field error */
  setFieldError: (name: keyof T, error: string) => void
  /** Set field touched */
  setFieldTouched: (name: keyof T, touched?: boolean) => void
  /** Validate single field */
  validateField: (name: keyof T) => Promise<string | undefined>
  /** Validate all fields */
  validateForm: () => Promise<boolean>
  /** Submit handler */
  handleSubmit: (e?: React.FormEvent) => Promise<void>
  /** Reset form to initial values */
  resetForm: () => void
  /** Set multiple values */
  setValues: (values: Partial<T>) => void
  /** Set multiple errors */
  setErrors: (errors: Partial<Record<keyof T, string>>) => void
}

/**
 * Default validation messages
 */
const DEFAULT_MESSAGES = {
  required: 'This field is required',
  minLength: (min: number) => `Must be at least ${min} characters`,
  maxLength: (max: number) => `Must be no more than ${max} characters`,
  pattern: 'Invalid format',
  email: 'Must be a valid email address',
  url: 'Must be a valid URL',
  min: (min: number) => `Must be at least ${min}`,
  max: (max: number) => `Must be no more than ${max}`,
  custom: 'Invalid value',
  async: 'Validation failed'
}

/**
 * Email validation regex
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * URL validation regex
 */
const URL_REGEX = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/

/**
 * Form validation hook
 */
export function useFormValidation<T extends Record<string, any>>({
  initialValues,
  validationSchema = {},
  onSubmit,
  validateOnChange = true,
  validateOnBlur = true,
  validateOnMount = false
}: UseFormValidationOptions<T>): UseFormValidationReturn<T> {
  const [values, setValuesState] = useState<T>(initialValues)
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({})
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  
  // Refs to avoid stale closures
  const valuesRef = useRef(values)
  valuesRef.current = values
  
  // Validate single field
  const validateField = useCallback(async (name: keyof T): Promise<string | undefined> => {
    const rules = validationSchema[name as string]
    if (!rules || rules.length === 0) return undefined
    
    const value = valuesRef.current[name]
    
    for (const rule of rules) {
      switch (rule.type) {
        case 'required':
          if (!value || (typeof value === 'string' && !value.trim())) {
            return rule.message || DEFAULT_MESSAGES.required
          }
          break
          
        case 'minLength':
          if (typeof value === 'string' && value.length < rule.value) {
            return rule.message || DEFAULT_MESSAGES.minLength(rule.value)
          }
          break
          
        case 'maxLength':
          if (typeof value === 'string' && value.length > rule.value) {
            return rule.message || DEFAULT_MESSAGES.maxLength(rule.value)
          }
          break
          
        case 'pattern':
          if (typeof value === 'string' && !rule.value.test(value)) {
            return rule.message || DEFAULT_MESSAGES.pattern
          }
          break
          
        case 'email':
          if (typeof value === 'string' && value && !EMAIL_REGEX.test(value)) {
            return rule.message || DEFAULT_MESSAGES.email
          }
          break
          
        case 'url':
          if (typeof value === 'string' && value && !URL_REGEX.test(value)) {
            return rule.message || DEFAULT_MESSAGES.url
          }
          break
          
        case 'min':
          if (typeof value === 'number' && value < rule.value) {
            return rule.message || DEFAULT_MESSAGES.min(rule.value)
          }
          break
          
        case 'max':
          if (typeof value === 'number' && value > rule.value) {
            return rule.message || DEFAULT_MESSAGES.max(rule.value)
          }
          break
          
        case 'custom':
          const result = rule.validator(value, valuesRef.current)
          if (result !== true) {
            return typeof result === 'string' ? result : rule.message || DEFAULT_MESSAGES.custom
          }
          break
          
        case 'async':
          setIsValidating(true)
          try {
            const asyncResult = await rule.validator(value, valuesRef.current)
            if (asyncResult !== true) {
              return typeof asyncResult === 'string' ? asyncResult : rule.message || DEFAULT_MESSAGES.async
            }
          } finally {
            setIsValidating(false)
          }
          break
      }
    }
    
    return undefined
  }, [validationSchema])
  
  // Validate all fields
  const validateForm = useCallback(async (): Promise<boolean> => {
    const newErrors: Partial<Record<keyof T, string>> = {}
    
    for (const field of Object.keys(validationSchema) as Array<keyof T>) {
      const error = await validateField(field)
      if (error) {
        newErrors[field] = error
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [validationSchema, validateField])
  
  // Handle field change
  const handleChange = useCallback((name: keyof T) => {
    return async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | any) => {
      let value: any
      
      if (event && event.target) {
        const target = event.target
        value = target.type === 'checkbox' ? target.checked : target.value
        
        // Convert to number if needed
        if (target.type === 'number') {
          value = value === '' ? '' : Number(value)
        }
      } else {
        value = event
      }
      
      setValuesState(prev => ({ ...prev, [name]: value }))
      
      if (validateOnChange && touched[name]) {
        const error = await validateField(name)
        setErrors(prev => ({ ...prev, [name]: error }))
      }
    }
  }, [validateOnChange, touched, validateField])
  
  // Handle field blur
  const handleBlur = useCallback((name: keyof T) => {
    return async () => {
      setTouched(prev => ({ ...prev, [name]: true }))
      
      if (validateOnBlur) {
        const error = await validateField(name)
        setErrors(prev => ({ ...prev, [name]: error }))
      }
    }
  }, [validateOnBlur, validateField])
  
  // Set field value manually
  const setFieldValue = useCallback(async (name: keyof T, value: any) => {
    setValuesState(prev => ({ ...prev, [name]: value }))
    
    if (validateOnChange && touched[name]) {
      const error = await validateField(name)
      setErrors(prev => ({ ...prev, [name]: error }))
    }
  }, [validateOnChange, touched, validateField])
  
  // Set field error manually
  const setFieldError = useCallback((name: keyof T, error: string) => {
    setErrors(prev => ({ ...prev, [name]: error }))
  }, [])
  
  // Set field touched manually
  const setFieldTouched = useCallback((name: keyof T, isTouched = true) => {
    setTouched(prev => ({ ...prev, [name]: isTouched }))
  }, [])
  
  // Submit handler
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }
    
    setIsSubmitting(true)
    
    // Mark all fields as touched
    const allTouched: Partial<Record<keyof T, boolean>> = {}
    for (const field of Object.keys(values) as Array<keyof T>) {
      allTouched[field] = true
    }
    setTouched(allTouched)
    
    // Validate form
    const isValid = await validateForm()
    
    if (isValid && onSubmit) {
      try {
        await onSubmit(values)
      } catch (error) {
        console.error('Form submission error:', error)
      }
    }
    
    setIsSubmitting(false)
  }, [values, validateForm, onSubmit])
  
  // Reset form
  const resetForm = useCallback(() => {
    setValuesState(initialValues)
    setErrors({})
    setTouched({})
    setIsSubmitting(false)
    setIsValidating(false)
  }, [initialValues])
  
  // Set multiple values
  const setValues = useCallback((newValues: Partial<T>) => {
    setValuesState(prev => ({ ...prev, ...newValues }))
  }, [])
  
  // Set multiple errors
  const setErrorsWrapper = useCallback((newErrors: Partial<Record<keyof T, string>>) => {
    setErrors(newErrors)
  }, [])
  
  // Calculate isValid
  const isValid = Object.keys(errors).length === 0
  
  // Validate on mount if requested
  useEffect(() => {
    if (validateOnMount) {
      validateForm()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  
  return {
    values,
    errors,
    touched,
    isValid,
    isSubmitting,
    isValidating,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    validateField,
    validateForm,
    handleSubmit,
    resetForm,
    setValues,
    setErrors: setErrorsWrapper
  }
}

export default useFormValidation